import React, {
  createContext,
  Dispatch,
  PropsWithChildren,
  SetStateAction,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import type { Dayjs } from 'dayjs';
import type { AlertColor } from '@mui/material/Alert';
import { getMarkets } from '../../../master-data';
import { useDateFormatter } from '../../../../shared/hooks/useDateFormatter';
import { useParamsBuilder } from '../../hooks/useVesselUtils';
import {
  ActiveScenario,
  useScenario,
} from '../../../../shared/contexts/ScenarioContext';
import ExpSnackbarAlerts from '../../../../shared/components/display/ExpSnackbarAlerts';
import { getErrorMessage } from '../../../../shared/utils/errorUtils';
import type { ApiRecord } from './types';

export interface ReportingFilters {
  departureCountry: string;
  departurePort: string;
  destinationCountry: string;
  destinationPort: string;
  startDateETD: Dayjs | null;
  endDateETD: Dayjs | null;
  startDateETA: Dayjs | null;
  endDateETA: Dayjs | null;
  shippingCompany: string;
  portOperator: string;
  groupBy: string;
  market: string | null;
  imo: string | null;
  search: string;
}

export interface ReportingFiltersContextValue {
  filters: ReportingFilters;
  setFilters: Dispatch<SetStateAction<ReportingFilters>>;
  markets: ApiRecord[];
  buildParams: () => URLSearchParams;
  activeScenario: ActiveScenario | null;
  showError: (error: unknown, methodName: string) => void;
}

interface SnackbarState {
  open: boolean;
  message: string;
  severity: AlertColor;
}

const ReportingFiltersContext =
  createContext<ReportingFiltersContextValue | null>(null);

export const ReportingFiltersProvider = ({ children }: PropsWithChildren) => {
  const [filters, setFilters] = useState<ReportingFilters>({
    departureCountry: '',
    departurePort: '',
    destinationCountry: '',
    destinationPort: '',
    startDateETD: null,
    endDateETD: null,
    startDateETA: null,
    endDateETA: null,
    shippingCompany: '',
    portOperator: '',
    groupBy: 'departure',
    market: null,
    imo: null,
    search: '',
  });
  const [markets, setMarkets] = useState<ApiRecord[]>([]);
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: '',
    severity: 'error',
  });

  const formatDate = useDateFormatter();
  const { activeScenario } = useScenario();
  const buildParams: () => URLSearchParams = useParamsBuilder(
    filters,
    formatDate,
    activeScenario
  );

  const handleSnackbarClose = () =>
    setSnackbar((prev) => ({ ...prev, open: false }));

  const showError = useCallback((error: unknown, methodName: string) => {
    setSnackbar({
      open: true,
      message: getErrorMessage(error, methodName),
      severity: 'error',
    });
  }, []);

  const fetchMarkets = useCallback(async () => {
    try {
      const response = await getMarkets();
      setMarkets(response || []);
    } catch (error) {
      showError(error, 'fetchMarkets');
      setMarkets([]);
    }
  }, [showError]);

  useEffect(() => {
    fetchMarkets();
  }, [fetchMarkets]);

  const value = useMemo<ReportingFiltersContextValue>(
    () => ({
      filters,
      setFilters,
      markets,
      buildParams,
      activeScenario,
      showError,
    }),
    [filters, markets, buildParams, activeScenario, showError]
  );

  return (
    <ReportingFiltersContext.Provider value={value}>
      <ExpSnackbarAlerts
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
      />
      {children}
    </ReportingFiltersContext.Provider>
  );
};

export const useReportingFilters = (): ReportingFiltersContextValue => {
  const ctx = useContext(ReportingFiltersContext);
  if (ctx === null) {
    throw new Error(
      'useReportingFilters must be used within a <ReportingFiltersProvider>'
    );
  }
  return ctx;
};
